
import React from "react";
import infoImage from "../../assets/images/info.svg";
import { CacheManager, Schemes, Utils } from "../../utils";
import { SetURLSearchParams } from "react-router-dom";
import LoadingGif from "../../assets/images/loading_circle.gif";
import { ApiResponseData, INotification, ISession } from "../../services";
import { Button, ButtonManageRef, Classname, Dropdown, FormControl, FormGroup, NoseurElement, NoseurObject, NumberInput, Paginator, PaginatorPageChangeOption, PaginatorPageElementOption, PaginatorProps, Popover, PopoverManageRef, Scheme, Transition } from "@rivtn/noseur";
import { i18nManager } from "../../i18n";

export const Elements = {

    FCFL: {
        fieldsetLegendBackground: "var(--pageBackgroundColor)",
    },

    LOADING_STATE: (<div style={{ margin: "90px 110px", display: "flex" }}>
        <img alt={"loading plans and features"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
    </div>),

    LOADING_STATE2: (<div style={{ margin: "auto", display: "flex" }}>
        <img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
    </div>),

    BUTTON_LOADING_PROPS: {
        disabled: true,
        leftIcon: "fas fa-spinner fa-pulse"
    },

    BUTTON_UPGRADE_PROPS: {
        readOnly: true,
        scheme: Schemes.GOLDEN,
        style: { padding: "10px 35px" },
        leftIcon: "fa fa-arrow-circle-up",
    },

    TREND_ICON: (<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.375 3.75V4.6875H12.4622L8.4375 8.71219L6.42516 6.69984C6.33725 6.61197 6.21805 6.5626 6.09375 6.5626C5.96945 6.5626 5.85025 6.61197 5.76234 6.69984L0.9375 11.5247L1.60031 12.1875L6.09375 7.69406L8.10609 9.70641C8.194 9.79428 8.3132 9.84365 8.4375 9.84365C8.5618 9.84365 8.681 9.79428 8.76891 9.70641L13.125 5.35031V8.4375H14.0625V3.75H9.375Z" fill="#06933E" />
    </svg>),

    selectSessionIcon(deviceClass: string) {
        if (deviceClass === "Robot") return "fa fa-robot";
        if (deviceClass === "Desktop") return "fa fa-desktop-alt";
        return "fa fa-mobile-alt";
    },

    buildConfirmDialogForm: (form: NoseurObject<any>[], onFormInput?: (name: string, value: any, search?: boolean) => any, formValue?: NoseurObject<any>, refer?: (name: string, ref: any) => void, styleOrClassname: React.CSSProperties | string = {}, optionsMap: any = {}, style2: React.CSSProperties = {}, setSearchParams?: SetURLSearchParams | URLSearchParams, cb?: Function, onSearch?: Function, queryKeyMap?: NoseurObject<string>) =>
    (<FormGroup className={Classname.build("form", (typeof styleOrClassname === "string" ? styleOrClassname : undefined))} scheme={Schemes.OMNEHOST} style={{ marginTop: 15, ...(typeof styleOrClassname === "string" ? {} : styleOrClassname) }}>
        {form?.map((input: NoseurObject<any>, index: number) => {
            let { name, type } = input;
            if (!input.options) input.options = optionsMap[name];
            name = queryKeyMap && (name in queryKeyMap) ? queryKeyMap[name] : name;
            const props = Utils.buildSelectNoseurComponentProps(input as any, formValue, setSearchParams, cb, name, onSearch, onFormInput);

            return (
                <FormControl isFieldset label={input.label} key={index} leftContent={input.icon} validStyle={{ borderColor: "var(--inputBorderColor)" }} contentStyle={{ borderRadius: 5 }}
                    style={type === "checkbox" ? { background: "none", ...style2 } : style2} ref={(el: any) => el && refer && refer(name, el)}>
                    {React.createElement(Utils.selectNoseurComponent(type) as any, props)}
                </FormControl>);
        })}
    </FormGroup>),

    refreshSessionDialogContent: (form: NoseurObject<any>[], errorRef: { current: any }, onFormInput?: (name: string, value: string) => void) => (<div style={{ marginTop: 10, marginBottom: 10 }}>
        {Elements.buildConfirmDialogForm(form, onFormInput)}
        <div ref={(r) => { errorRef!.current = r; }} className="error"> </div>
    </div>),

    buildPaginator: (getter: ApiResponseData<any>, onPageChange?: (event: PaginatorPageChangeOption, newPageSize?: string) => void, initialPage?: number, style: React.CSSProperties = {}) => (<div className="paginator" style={style}>
        <Paginator freezeTotalRecords totalRecords={getter.total_elements} rowsPerPage={getter.size} template={{
            layout: "Showing SizeElement of TotalRecords", customElement: (layout: string, props?: PaginatorProps) => {
                if (layout !== "SizeElement") return ` ${layout} `;
                return (<NumberInput key={Utils.randomString(5)} defaultValue={getter.size} completeInputOnBlurOnly
                    onInputComplete={(value) => onPageChange?.({ currentPage: initialPage } as any, value)} />);
            }
        }} />
        <Paginator freezeTotalRecords totalRecords={getter.total_elements} rowsPerPage={getter.size} initialPage={initialPage} template={{
            layout: "FirstPageElement PreviousPageElement PreviousPagesElement PageElements NextPagesElement NextPageElement LastPageElement",
            firstPageElement: (option: PaginatorPageElementOption) => (<i key={"k1"} onClick={option.onClick} className="fa fa-angle-left" style={{ margin: "0px 8px", cursor: "pointer", marginLeft: 40 }}></i>),
            lastPageElement: (option: PaginatorPageElementOption) => (<i key={"k2"} onClick={option.onClick} className="fa fa-angle-right" style={{ margin: "0px 8px", cursor: "pointer", marginRight: 40 }}></i>),
            previousPageElement: (option: PaginatorPageElementOption) => (<span key={"k3"} onClick={option.onClick} style={{ margin: "0px 8px", cursor: "pointer" }}>{i18nManager.Labels.common.prev}</span>),
            nextPageElement: (option: PaginatorPageElementOption) => (<span key={"k4"} onClick={option.onClick} style={{ margin: "0px 8px", cursor: "pointer", marginLeft: 10 }}>{i18nManager.Labels.common.next}</span>),
        }} onPageChange={(e) => onPageChange?.(e, (getter?.size ? `${getter?.size}` : ""))} />
    </div>),

    buildSorter: (options: { label: string; value: string; }[], onSelectOption: (value: string) => void, defaultValue?: string, style: React.CSSProperties = { marginTop: 10 }) => {
        return (<Dropdown borderless scheme={Schemes.OMNEHOST} style={{ width: 40, fontSize: 13 }}
            defaultInputValue={defaultValue} // todo in noseur add props match input value default to selected 
            formControlProps={{ style, leftContent: (<span className="sorter-l">{i18nManager.Labels.common.sort_by}</span>) }} options={[
                ...options,
                {
                    value: "",
                    label: i18nManager.Labels.common.none,
                }
            ]} onSelectOption={(option) => onSelectOption(option.value)} />);
    },

    buildEmptyListView: (icon?: string, desc?: React.ReactNode, style?: React.CSSProperties) => (<div className="empty-list-view" style={style}>
        {icon && (<i className={icon} />)}
        {desc}
    </div>),

    buildPulsar(style?: React.CSSProperties) {
        return (<div className={Classname.build("pulsar", Transition.BEAT, Transition.INFINITE)} style={style}>
            <div>
                <div className="pulsar" style={{ "--pulsarSize": "12px" } as any}>
                    <div>
                        <div className="pulsar" style={{ "--pulsarSize": "5px" } as any}>
                            <div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>);
    },

    buildSnapper(onClick: (e?: any) => void, disabled?: boolean) {
        return (<svg className={Classname.build(disabled ? "noseur-disabled" : undefined)} width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={onClick} style={{ cursor: "pointer" }}>
            <path d="M8.41797 16.8337V10.9953C8.41797 10.8627 8.48382 10.7355 8.60103 10.6418C8.71824 10.548 8.87721 10.4953 9.04297 10.4953H10.2648C10.3693 10.4953 10.4721 10.4743 10.5638 10.4342C10.6555 10.3942 10.7333 10.3364 10.7898 10.2662L12.2961 8.39616C12.3527 8.3259 12.4304 8.26811 12.5221 8.22808C12.6138 8.18804 12.7166 8.16704 12.8211 8.16699H16.5148C16.6193 8.16704 16.7221 8.18804 16.8138 8.22808C16.9055 8.26811 16.9833 8.3259 17.0398 8.39616L18.5461 10.2662C18.6027 10.3364 18.6804 10.3942 18.7721 10.4342C18.8638 10.4743 18.9666 10.4953 19.0711 10.4953H20.293C20.375 10.4953 20.4563 10.5083 20.5321 10.5334C20.608 10.5585 20.6769 10.5953 20.7349 10.6418C20.7929 10.6882 20.839 10.7433 20.8704 10.804C20.9018 10.8646 20.918 10.9297 20.918 10.9953V16.8337C20.918 16.8993 20.9018 16.9643 20.8704 17.025C20.839 17.0857 20.7929 17.1408 20.7349 17.1872C20.6769 17.2336 20.608 17.2705 20.5321 17.2956C20.4563 17.3207 20.375 17.3337 20.293 17.3337H9.04297C8.96089 17.3337 8.87962 17.3207 8.80379 17.2956C8.72796 17.2705 8.65906 17.2336 8.60103 17.1872C8.54299 17.1408 8.49695 17.0857 8.46554 17.025C8.43413 16.9643 8.41797 16.8993 8.41797 16.8337Z" stroke="#CA2A6A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1.125 14V16.5H3.72917M1.125 6.91667V11.0833M1.125 4V1.5H4.25M7.89583 1.5H13.1042M16.75 1.5H19.875V3.58333M19.875 7.33333V6.08333M14.6667 14.8333C15.2192 14.8333 15.7491 14.6577 16.1398 14.3452C16.5305 14.0326 16.75 13.6087 16.75 13.1667C16.75 12.7246 16.5305 12.3007 16.1398 11.9882C15.7491 11.6756 15.2192 11.5 14.6667 11.5C14.1141 11.5 13.5842 11.6756 13.1935 11.9882C12.8028 12.3007 12.5833 12.7246 12.5833 13.1667C12.5833 13.6087 12.8028 14.0326 13.1935 14.3452C13.5842 14.6577 14.1141 14.8333 14.6667 14.8333Z" stroke="#CA2A6A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>);
    },

    buildShareToView() {
        return (<>
            <span className="sh">{i18nManager.Labels.teams.dialog.share_to}</span>
            <div className="d-socials" style={{ display: "flex", flexWrap: "wrap" }}>
                <i className="fa fa-comment" />
                <i className="fab fa-x-twitter" />
                <i className="fab fa-instagram" style={{ color: "#c46b54" }} />
                <i className="fab fa-whatsapp" style={{ color: "#33b544" }} />
                <i className="fab fa-facebook" style={{ color: "#2c5ca3" }} />
            </div>
        </>);
    },

};
